import {
  N_ArtworkSpec,
  N_Duration,
  N_NamedAnnotated,
} from "@specsheet-common/shared-types";
import { notNull } from "../../typescript";
import { joinNames } from "./joinNames";

/** Current attribute name on digital static (matches campaign "Spot Length" column label). */
const SPOT_LENGTH_ATTRIBUTE_NAME = "Spot Length";
/** Legacy imports used "Slot length"; still read for existing placements. */
const LEGACY_SLOT_LENGTH_ATTRIBUTE_NAME = "Slot length";

const findSpotLengthAttribute = (
  attributes: Array<{ name: string; value: string }>
) =>
  attributes.find(
    (a) =>
      a.name === SPOT_LENGTH_ATTRIBUTE_NAME ||
      a.name === LEGACY_SLOT_LENGTH_ATTRIBUTE_NAME
  );

const getDuration = (duration: N_Duration): string =>
  (duration.hours !== null ? `${duration.hours}h` : "") +
  (duration.minutes !== null ? ` ${duration.minutes}m` : "") +
  (duration.seconds !== null ? ` ${duration.seconds}s` : "") +
  (duration.milliSeconds !== null ? ` ${duration.milliSeconds}ms` : "");

const slotLengthAttributeValueToDuration = (raw: string): N_Duration | null => {
  const s = raw.trim();
  const numMatch = s.match(/^(\d+(?:\.\d+)?)/);
  if (!numMatch) return null;
  const n = parseFloat(numMatch[1]);
  if (!(n > 0)) return null;
  const rest = s.slice(numMatch[0].length).trim().toLowerCase();
  if (!rest || /^s(ec|ecs|econd|econds)?$/.test(rest)) {
    return { hours: null, minutes: null, seconds: n, milliSeconds: null };
  }
  if (/^m(in|ins|inute|inutes)?$/.test(rest)) {
    return { hours: null, minutes: n, seconds: null, milliSeconds: null };
  }
  if (/^h(r|rs|our|ours)?$/.test(rest)) {
    return { hours: n, minutes: null, seconds: null, milliSeconds: null };
  }
  return { hours: null, minutes: null, seconds: n, milliSeconds: null };
};

export const getDurations = (
  specifications: N_NamedAnnotated<N_ArtworkSpec>[]
): N_NamedAnnotated<string>[] =>
  specifications.reduce<N_NamedAnnotated<string>[]>((durations, spec) => {
    const fromAnimatedHtml5VideoAudio =
      (spec.value.kind === "animated" ||
        spec.value.kind === "html5" ||
        spec.value.kind === "video" ||
        spec.value.kind === "audio") &&
      spec.value.value.durations !== null
        ? [
            ...spec.value.value.durations.value,
            ...(spec.value.kind === "html5" &&
            notNull(spec.value.value.formats.value.video) &&
            notNull(spec.value.value.formats.value.video.value.video.durations)
              ? spec.value.value.formats.value.video.value.video.durations.value
              : []),
          ].map((d) => ({
            ...spec,
            name: joinNames(spec.name, d.name),
            value: getDuration(d.value),
          }))
        : [];

    const fromDigitalStaticSlotLength =
      spec.value.kind === "digitalStatic"
        ? (() => {
            const attr = findSpotLengthAttribute(spec.value.value.attributes);
            if (!attr?.value?.trim()) return [];
            const d = slotLengthAttributeValueToDuration(attr.value);
            if (!d) return [];
            return [
              {
                ...spec,
                name: joinNames(spec.name, SPOT_LENGTH_ATTRIBUTE_NAME),
                value: getDuration(d),
              },
            ];
          })()
        : [];

    return [
      ...durations,
      ...fromAnimatedHtml5VideoAudio,
      ...fromDigitalStaticSlotLength,
    ];
  }, []);
